"use client"

/**
 * Client-side auth guard — render-gating only; it never redirects. Every 401
 * is handled by the global ``QueryCache``/``MutationCache`` handler
 * (``lib/auth/session-expiry.ts``), the single place that sends a user to
 * ``/signin`` (design §1.1).
 *
 *   | ``useCurrentUser()`` state    | render                                  |
 *   |-------------------------------|-----------------------------------------|
 *   | success                       | children                                |
 *   | pending                       | <FullScreenLoader/>                     |
 *   | 401 error (handler redirects) | <FullScreenLoader/>                     |
 *   | non-401 error (500 / network) | children — let the page's error UI show |
 *
 * The loader keeps the empty dashboard shell from flashing before a redirect
 * (§1.5); the non-401 row keeps a broken ``/api/auth/me/`` from trapping the
 * user behind an infinite loader.
 *
 * bfcache (§1.6): the loader is never captured into an authenticated
 * snapshot, because ``useCurrentUser``'s 5-minute ``staleTime`` makes in-app
 * navigations render children immediately and cold loads bypass bfcache;
 * ``BfcacheRevalidator`` re-resolves the gate on a restore.
 */

import { useEffect, type ReactNode } from "react"
import { usePathname } from "next/navigation"

import { useCurrentUser } from "@/features/auth/hooks"
import { isPublicRoute } from "@/lib/auth/public-routes"
import { markSessionEstablished } from "@/lib/auth/session-expiry"
import { ApiError } from "@/lib/api/client"
import { setDatadogUser } from "@/lib/observability/datadog"
import { FullScreenLoader } from "@/components/full-screen-loader"

function AuthGate({ children }: { children: ReactNode }) {
  const { data: user, error, isPending, isSuccess } = useCurrentUser()

  useEffect(() => {
    if (isSuccess) {
      // A later 401 then reads as a mid-session expiry (the "signed out"
      // notice) rather than a first-time gate.
      markSessionEstablished()
    }
  }, [isSuccess])

  useEffect(() => {
    if (user) {
      setDatadogUser(user.id)
    }
  }, [user])

  if (isSuccess) {
    return <>{children}</>
  }

  const unauthorized = error instanceof ApiError && error.status === 401
  if (isPending || unauthorized) {
    return <FullScreenLoader />
  }

  return <>{children}</>
}

export function AuthGuard({ children }: { children: ReactNode }) {
  const pathname = usePathname() ?? "/"
  if (isPublicRoute(pathname)) {
    return <>{children}</>
  }
  return <AuthGate>{children}</AuthGate>
}
